import {
  Button,
  Col,
  Modal,
  Row,
  Spin,
  Table,
  Tag,
  Space,
  Tooltip,
  Select,
  Radio,
  List,
  theme,
  Typography,
  Checkbox,
  Popover,
  DatePicker,
  Dropdown,
  Input,
  message,
  Upload,
  Progress,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useUser } from "../../Context/UserContext";
import {
  AppstoreOutlined,
  LoadingOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  BranchesOutlined,
  DeleteOutlined,
  InboxOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  ReloadOutlined,
  PlusOutlined,
  TeamOutlined,
  SyncOutlined,
  HolderOutlined,
  MoreOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  SwapOutlined,
  LinkOutlined,
  CalendarOutlined,
  UnderlineOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table/interface";
import dayjs from "dayjs";
import TicketServices, {
  TicketModule,
  TicketStatus,
  TicketPriority,
  CloseReason,
  CLOSED_REASONS,
  MergeType,
  DateFormats,
  TicketDependencyType,
  TicketIssueType,
  TicketModuleDisplay,
} from "../../Services/TicketServices";
import UserService from "../../Services/UserService";
import { ApplicationUserStatus } from "../../Common/CommonComponent";
import { adminContext } from "../../Common/PageRoute";
import { PageLayout, PageHeader, SectionCard, TableToolbar } from "../../Common/Layout";
import CreateTicketPanel from "./CreateTicketPanel";
import { useNavigate } from "react-router-dom";
import { useResizableColumns } from "../../Common/Custom Components/Ant design/useResizableColumns";
import { usePractice } from "../../Context/PracticeContext";
const { Text } = Typography;
const { TextArea } = Input;

const InputRequiredIcon = <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 14" fill="none" focusable="false"><path d="M5.16667 9.83268L8.5 6.49935M8.5 6.49935L5.16667 3.16602M8.5 6.49935H0.5" stroke="#787878" stroke-linecap="round"></path><path d="M2.98828 8.52527V10.5273C2.98828 11.4702 2.98828 11.9416 3.28117 12.2345C3.57407 12.5273 4.04547 12.5273 4.98828 12.5273H10.0078C10.9506 12.5273 11.422 12.5273 11.7149 12.2345C12.0078 11.9416 12.0078 11.4702 12.0078 10.5273V2.5C12.0078 1.55719 12.0078 1.08579 11.7149 0.792893C11.422 0.5 10.9506 0.5 10.0078 0.5H4.98828C4.04547 0.5 3.57407 0.5 3.28117 0.792893C2.98828 1.08579 2.98828 1.55719 2.98828 2.5V4.54149" stroke="#787878" stroke-linecap="round"></path></svg>;

const allowedTransitionsMap: Record<number, number[]> = {
  [TicketStatus.Open]: [TicketStatus.Closed, TicketStatus.UnderResearch, TicketStatus.InProgress, TicketStatus.InTesting, TicketStatus.ReadyForRelease],
  [TicketStatus.Closed]: [],
  [TicketStatus.UnderResearch]: [TicketStatus.Closed, TicketStatus.InProgress, TicketStatus.InTesting, TicketStatus.ReadyForRelease],
  [TicketStatus.InProgress]: [TicketStatus.Closed, TicketStatus.UnderResearch, TicketStatus.InTesting, TicketStatus.ReadyForRelease],
  [TicketStatus.InTesting]: [TicketStatus.Closed, TicketStatus.UnderResearch, TicketStatus.InProgress, TicketStatus.ReadyForRelease],
  [TicketStatus.ReadyForRelease]: [TicketStatus.Closed, TicketStatus.UnderResearch, TicketStatus.InProgress, TicketStatus.InTesting],
};

// ── Status KPI config ────────────────────────────────────────────────────────
const KPI_STATUSES = [
  { status: -1, label: "Total", icon: <AppstoreOutlined />, bgLight: "#EEF2FF", bgDark: "#312E81", fg: "#4F46E5", antColor: "geekblue" },
  { status: TicketStatus.Open, label: "Open", icon: <InboxOutlined />, bgLight: "#EFF6FF", bgDark: "#1D3461", fg: "#3B82F6", antColor: "processing" },
  { status: TicketStatus.InProgress, label: "In progress", icon: <ClockCircleOutlined />, bgLight: "#F5F3FF", bgDark: "#2D1B69", fg: "#8B5CF6", antColor: "purple" },
  { status: TicketStatus.UnderResearch, label: "Under research", icon: <ClockCircleOutlined />, bgLight: "#ECFEFF", bgDark: "#082F49", fg: "#06B6D4", antColor: "cyan" },
  { status: TicketStatus.InTesting, label: "In testing", icon: <ClockCircleOutlined />, bgLight: "#FFFBEB", bgDark: "#451A03", fg: "#F59E0B", antColor: "warning" },
  { status: TicketStatus.ReadyForRelease, label: "Ready for release", icon: <ClockCircleOutlined />, bgLight: "#ECFDF5", bgDark: "#064E3B", fg: "#10B981", antColor: "success" },
  { status: TicketStatus.Closed, label: "Closed", icon: <CheckCircleOutlined />, bgLight: "#F9FAFB", bgDark: "#111827", fg: "#6B7280", antColor: "default" },
];

const calculatePercentages = (statusCounts: Record<number, number>, statuses: typeof KPI_STATUSES) => {
  const total = statuses.reduce((sum, k) => sum + (statusCounts[k.status] ?? 0), 0);
  if (total === 0) return {};

  const exactPcts = statuses.map(k => ({
    status: k.status,
    raw: ((statusCounts[k.status] ?? 0) / total) * 100,
  }));

  const floorPcts = exactPcts.map(item => ({
    status: item.status,
    floor: Math.floor(item.raw),
    remainder: item.raw - Math.floor(item.raw),
  }));

  const currentSum = floorPcts.reduce((sum, item) => sum + item.floor, 0);
  let remainderNeeded = 100 - currentSum;

  const sortedByRemainder = [...floorPcts].sort((a, b) => b.remainder - a.remainder);
  const addOneTo = new Set(sortedByRemainder.slice(0, remainderNeeded).map(item => item.status));

  const result: Record<number, number> = {};
  floorPcts.forEach(item => {
    result[item.status] = item.floor + (addOneTo.has(item.status) ? 1 : 0);
  });
  return result;
};

// ── Mini KPI card ─────────────────────────────────────────────────────────────
const KpiCard: React.FC<{
  kpi: (typeof KPI_STATUSES)[0]; count: number; loading: boolean;
  active: boolean; isDark: boolean; onClick: () => void;
}> = ({ kpi, count, loading, active, isDark, onClick }) => {
  const bg = isDark ? kpi.bgDark : kpi.bgLight;
  return (
    <div
      onClick={onClick}
      style={{
        background: bg,
        border: `1.5px solid ${active ? kpi.fg : kpi.fg + "30"}`,
        borderRadius: 10,
        padding: "10px 12px",
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        display: "flex",
        alignItems: "center",
        gap: 8,
        boxShadow: active ? `0 0 0 2px ${kpi.fg}40` : "none",
        minWidth: 0,
        width: "100%",
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)"; (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 12px ${kpi.fg}30`; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)"; if (!active) (e.currentTarget as HTMLDivElement).style.boxShadow = "none"; }}
    >
      <div style={{ width: 32, height: 32, borderRadius: 8, background: `${kpi.fg}20`, display: "flex", alignItems: "center", justifyContent: "center", color: kpi.fg, fontSize: 14, flexShrink: 0 }}>
        {kpi.icon}
      </div>
      <div style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
        <Tooltip title={kpi.label}>
          <div style={{ fontSize: 11, color: isDark ? "#9CA3AF" : "#6B7280", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {kpi.label}
          </div>
        </Tooltip>
        {loading
          ? <Spin indicator={<LoadingOutlined style={{ fontSize: 12, color: kpi.fg }} spin />} />
          : <div style={{ fontSize: 18, fontWeight: 700, color: kpi.fg, lineHeight: 1.2 }}>{count.toLocaleString()}</div>
        }
      </div>
    </div>
  );
};
const AOTicketDashboard = () => {
  const { openNotification } = React.useContext(adminContext);
  const navigate = useNavigate();
  const {allPracticeIds} = usePractice();
  const { token } = theme.useToken();
  const isDark = token.colorTextBase === "#E9EEF9";
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);

  const [filters, setFilters] = useState({
    search: "",
    status: TicketStatus.Active,
    priority: 0,
    inputRequired: "all" as "all" | "true" | "false",
    closeReason: undefined as number | undefined,
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
    sortCol: "CreatedBy.Date",
    sortDir: "desc" as "asc" | "desc",
    practiceId: 0,
    agentId: "",
    type: 0,
    module: 0
  });

  const dateRangeStr = filters.dateRange
    ? `${filters.dateRange[0].format("DD/MM/YYYY")}_${filters.dateRange[1].format("DD/MM/YYYY")}`
    : "";

  // ── KPI counts ──────────────────────────────────────────────────────────────
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [countsLoading, setCountsLoading] = useState(true);
  const [showMergedTickets, setShowMergedTickets] = React.useState<string>('')
  const fetchCounts = useCallback(async () => {
    setCountsLoading(true);
    try {
      const fromDateStr = filters.dateRange ? filters.dateRange[0].format("DD/MM/YYYY") : "";
      const toDateStr = filters.dateRange ? filters.dateRange[1].format("DD/MM/YYYY") : "";
      const results = await Promise.all(
        KPI_STATUSES.map(k =>
          TicketServices.getList(0, 1, k.status, "", "CreatedBy.Date", "desc", 0, "", fromDateStr, toDateStr, 0, null)
        )
      );
      const map: Record<number, number> = {};
      KPI_STATUSES.forEach((k, i) => { map[k.status] = (results[i] as any)?.totalRecords ?? 0; });
      setCounts(map);
    } catch { /* silent */ }
    finally { setCountsLoading(false); }
  }, [dateRangeStr]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const [pagingItems, setPagingItems] = useState({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 10,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedCloseReason, setSelectedCloseReason] = useState<number>(CloseReason.Unknown);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [updateLinkedTickets, setUpdateLinkedTickets] = useState(false);

  // Merge modal states
  const [isMergeOpen, setIsMergeOpen] = useState(false);

  // Create ticket modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [mergeTicketsQueue, setMergeTicketsQueue] = useState<any[]>([]);
  const [mergeLoadingOptions, setMergeLoadingOptions] = useState(false);
  const [mergeTicketOptions, setMergeTicketOptions] = useState<any[]>([]);
  const [mergeSearchValue, setMergeSearchValue] = useState<string | undefined>(undefined);
  const [mergeParentStatus, setMergeParentStatus] = useState<number>(0);
  const [mergeChildStatus, setMergeChildStatus] = useState<number>(TicketStatus.Closed);
  const [mergeType, setMergeType] = useState<number>(MergeType.SeparateThreads); // SeparateThreads = 2
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // ── Selection & Batch Actions states ─────────────────────────────────────────
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // Merge / Link Modal mode state
  const [isLinkMode, setIsLinkMode] = useState(false);

  // Batch Status Modal states
  const [isBatchStatusOpen, setIsBatchStatusOpen] = useState(false);
  const InitialBatchStatusValue = { closeReason: CloseReason.Unknown, customCloseReason: '', status: TicketStatus.Unknown, ticketIds: [], updateLinked: false }
  const [batchStatusValues, setBatchStatusValues] = useState<{ status: TicketStatus, ticketIds: string[], closeReason: number, customCloseReason: string, updateLinked: boolean }>(InitialBatchStatusValue);

  // Batch Assign Modal states
  const [isBatchAssignOpen, setIsBatchAssignOpen] = useState(false);
  const [batchAssigneeIds, setBatchAssigneeIds] = useState<string[]>([]);
  const [assigneeOptions, setAssigneeOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [assigneeLoading, setAssigneeLoading] = useState(false);

  // Single ticket actions modal states
  const [actionTicket, setActionTicket] = useState<any>(null);

  // Reopen
  const [isReopenOpen, setIsReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  // Request Input
  const [isInputReqOpen, setIsInputReqOpen] = useState(false);
  const [inputReqReason, setInputReqReason] = useState("");

  // Single Assign
  const [isSingleAssignOpen, setIsSingleAssignOpen] = useState(false);
  const [singleAssigneeId, setSingleAssigneeId] = useState("");

  // Single ETA
  const [isSingleEtaOpen, setIsSingleEtaOpen] = useState(false);
  const [singleEtaDate, setSingleEtaDate] = useState<dayjs.Dayjs | null>(null);



  const handleActionMenuClick = (key: string, row: any) => {
    setActionTicket(row);
    if (key === "close") {
      openModal(row);
    } else if (key === "reopen") {
      setReopenReason("");
      setIsReopenOpen(true);
    } else if (key === "requestInput") {
      setInputReqReason("");
      setIsInputReqOpen(true);
    } else if (key === "cancelInput") {
      Modal.confirm({
        title: "Cancel input required",
        content: `Are you sure you want to cancel the input required status for ticket #${row.number}?`,
        okText: "Yes",
        cancelText: "No",
        onOk: async () => {
          try {
            setSubmitting(true);
            const res = await TicketServices.setInputRequired(row.id, false);
            if (res.status) {
              openNotification("success", "Input required status cancelled successfully.");
              fetchData(pagingItems.currentPage, pagingItems.pageSize);
            } else {
              openNotification("error", res.message || "Failed to cancel input required.");
            }
          } catch (err) {
            openNotification("error", "Error cancelling input required.");
          } finally {
            setSubmitting(false);
          }
        }
      });
    } else if (key === "assignAgent") {
      setSingleAssigneeId(row.assignedTo?.id || "");
      fetchAssigneeOptions();
      setIsSingleAssignOpen(true);
    } else if (key === "resolutionEta") {
      setSingleEtaDate(row.resolved?.resolutionETA ? dayjs(row.resolved.resolutionETA) : null);
      setIsSingleEtaOpen(true);
    }
  };

  const handleReopenConfirm = async () => {
    if (!actionTicket || reopenReason.trim().length < 5) return;
    try {
      setSubmitting(true);
      const res = await TicketServices.reopen(actionTicket.id, reopenReason.trim());
      if (res.status) {
        openNotification("success", "Ticket reopened successfully.");
        setIsReopenOpen(false);
        setReopenReason("");
        setActionTicket(null);
        fetchData(pagingItems.currentPage, pagingItems.pageSize);
      } else {
        openNotification("error", res.message || "Failed to reopen ticket.");
      }
    } catch (err) {
      openNotification("error", "Error reopening ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputReqConfirm = async () => {
    if (!actionTicket || !inputReqReason.trim()) return;
    try {
      setSubmitting(true);
      const res = await TicketServices.setInputRequired(actionTicket.id, true, inputReqReason.trim());
      if (res.status) {
        openNotification("success", "Input request sent successfully.");
        setIsInputReqOpen(false);
        setInputReqReason("");
        setActionTicket(null);
        fetchData(pagingItems.currentPage, pagingItems.pageSize);
      } else {
        openNotification("error", res.message || "Failed to request client input.");
      }
    } catch (err) {
      openNotification("error", "Error requesting client input.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleAssignConfirm = async () => {
    if (!actionTicket) return;
    try {
      setSubmitting(true);
      const res = await TicketServices.updateAgent(actionTicket.id, singleAssigneeId);
      if (res.status) {
        openNotification("success", "Agent assigned successfully.");
        setIsSingleAssignOpen(false);
        setSingleAssigneeId("");
        setActionTicket(null);
        fetchData(pagingItems.currentPage, pagingItems.pageSize);
      } else {
        openNotification("error", res.message || "Failed to assign agent.");
      }
    } catch (err) {
      openNotification("error", "Error assigning agent.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleEtaConfirm = async () => {
    if (!actionTicket || !singleEtaDate) return;
    try {
      setSubmitting(true);
      const res = await TicketServices.setResolutionETA(actionTicket.id, singleEtaDate.format("DD/MM/YYYY"));
      if (res.status) {
        openNotification("success", "Resolution ETA updated successfully.");
        setIsSingleEtaOpen(false);
        setSingleEtaDate(null);
        setActionTicket(null);
        fetchData(pagingItems.currentPage, pagingItems.pageSize);
      } else {
        openNotification("error", res.message || "Failed to update resolution ETA.");
      }
    } catch (err) {
      openNotification("error", "Error updating resolution ETA.");
    } finally {
      setSubmitting(false);
    }
  };

  const getBatchStatusOptions = () => {
    if (selectedRows.length === 0) return [];
    const firstStatus = selectedRows[0].status;
    let commonTransitions = allowedTransitionsMap[firstStatus] || [];
    for (let i = 1; i < selectedRows.length; i++) {
      const status = selectedRows[i].status;
      const allowed = allowedTransitionsMap[status] || [];
      commonTransitions = commonTransitions.filter(val => allowed.includes(val));
    }
    return statusOptions.filter(o => o.value !== 0 && commonTransitions.includes(o.value));
  };

  const getMergeParentStatusOptions = () => {
    if (mergeTicketsQueue.length === 0) return [{ label: "Current status", value: 0 }];
    const parentStatus = mergeTicketsQueue[0].status;
    const allowed = allowedTransitionsMap[parentStatus] || [];
    return [
      { label: "Current status", value: 0 },
      ...statusOptions.filter(o => o.value !== 0 && allowed.includes(o.value))
    ];
  };

  const fetchAssigneeOptions = useCallback(async () => {
    setAssigneeLoading(true);
    try {
      const res = await UserService.getUserOptions(true);
      if (res?.status) {
        const options = (res?.result?.items || [])
          .filter((item: any) => item?.id)
          .map((item: any) => ({
            label: item?.name,
            value: String(item?.id),
          }));
        setAssigneeOptions(options);
      }
    } catch { /* silent */ }
    finally { setAssigneeLoading(false); }
  }, []);

  useEffect(() => {
    fetchAssigneeOptions();
  }, [fetchAssigneeOptions]);

  const openModal = (row: any) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
    setReason("");
    setSelectedCloseReason(CloseReason.Unknown);
    setSubmitting(false);
    setUpdateLinkedTickets(false);
  };

  const handleAction = async () => {
    if (!selectedRow?.id) return;
    if (selectedCloseReason === CloseReason.Unknown) {
      openNotification("warning", "Please select a closed reason.");
      return;
    }
    if (selectedCloseReason === CloseReason.Other && reason.trim().length < 5) {
      openNotification("warning", "Please enter a reason (min 5 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await TicketServices.close(selectedRow.id, reason, updateLinkedTickets);

      if (res?.status) {
        openNotification("success", res.message || "Action successful.");
        closeModal();
        fetchData(pagingItems.currentPage, pagingItems.pageSize);
      } else {
        openNotification("error", res?.message || "Action failed.");
      }
    } catch (error) {
      openNotification("error", "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchData = async (page: number = 1, size: number = pagingItems.pageSize) => {
    setLoading(true);
    // Optimistically update page/size so pagination controls reflect the new state immediately
    setPagingItems(prev => ({ ...prev, currentPage: page, pageSize: size }));
    try {
      const start = (page - 1) * size;
      const inputReq = filters.inputRequired === "all" ? null : filters.inputRequired === "true";
      const fromDateStr = filters.dateRange ? filters.dateRange[0].format("DD/MM/YYYY") : "";
      const toDateStr = filters.dateRange ? filters.dateRange[1].format("DD/MM/YYYY") : "";

      const res: any = await TicketServices.getList(
        start,
        size,
        filters.status,
        filters.search,
        filters.sortCol,
        filters.sortDir,
        filters.priority,
        "",
        fromDateStr,
        toDateStr,
        filters.type,
        inputReq,
        filters?.module,
        filters.closeReason,
        filters.practiceId,
        filters?.agentId
      );

      if (res) {
        let items = res.items || [];
        setData(items);
        setPagingItems(prev => ({ ...prev, totalRecords: res.totalRecords || 0 }));
        // Reset selection on fetch
        setSelectedRowKeys([]);
        setSelectedRows([]);
      }
    } catch (error) {
      openNotification("error", "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    setLoading(true);
    try {
      const fromDateStr = filters.dateRange ? filters.dateRange[0].format("DD/MM/YYYY") : "";
      const toDateStr = filters.dateRange ? filters.dateRange[1].format("DD/MM/YYYY") : "";
      const inputReq = filters.inputRequired === "all" ? null : filters.inputRequired === "true";
      const res = await TicketServices.downloadList(
        filters.status,
        filters.search,
        filters.sortCol,
        filters.sortDir,
        filters.priority,
        filters.type,
        fromDateStr,
        toDateStr,
        inputReq,
        filters.closeReason,
        filters.practiceId,
        filters.agentId,
        filters.module
      );
      if (res && res.status === 'done') {
        openNotification("success", "CSV report downloaded successfully.");
      } else {
        openNotification("error", res?.error || "Download failed.");
      }
    } catch (err) {
      openNotification("error", "Download failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchMergeTicket = async (val: string) => {
    if (val.trim().length < 2) return;
    setMergeLoadingOptions(true);
    try {
      const res = await TicketServices.getList(
        0,
        10,
        0,
        val,
        "CreatedBy.Date",
        "desc",
        0,
        "",
        "",
        "",
        0,
        null,
      );
      const opts = (res?.items || [])
        .filter((t: any) => t.status !== TicketStatus.Closed)
        .map((t: any) => ({
          label: `${t.number} - ${t.title}`,
          value: t.id,
          status: t.status,
          number: t.number,
          title: t.title,
        }));
      setMergeTicketOptions(opts);
    } catch (err) {
      openNotification("error", "Failed to search tickets");
    } finally {
      setMergeLoadingOptions(false);
    }
  };

  const loadInitialMergeOptions = async () => {
    setMergeLoadingOptions(true);
    try {
      const res = await TicketServices.getList(
        0,
        50,
        0,
        "",
        "CreatedBy.Date",
        "desc",
        0,
        "",
        "",
        "",
        0,
        null
      );
      const opts = (res?.items || [])
        .filter((t: any) => t.status !== TicketStatus.Closed)
        .map((t: any) => ({
          label: `${t.number} - ${t.title}`,
          value: t.id,
          status: t.status,
          number: t.number,
          title: t.title,
        }));
      setMergeTicketOptions(opts);
    } catch (err) {
      const opts = data
        .filter((t: any) => t.status !== TicketStatus.Closed)
        .map((t: any) => ({
          label: `${t.number} - ${t.title}`,
          value: t.id,
          status: t.status,
          number: t.number,
          title: t.title,
        }));
      setMergeTicketOptions(opts);
    } finally {
      setMergeLoadingOptions(false);
    }
  };

  const openMergeOrLinkModal = (isLink: boolean) => {
    setIsLinkMode(isLink);
    const queue = selectedRows.map((t) => ({
      label: `${t.number} - ${t.title}`,
      value: t.id,
      status: t.status,
      number: t.number,
      title: t.title,
    }));
    setMergeTicketsQueue(queue);
    setMergeSearchValue(undefined);
    loadInitialMergeOptions();
    setIsMergeOpen(true);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const updatedQueue = [...mergeTicketsQueue];
    const [draggedItem] = updatedQueue.splice(draggedIndex, 1);
    updatedQueue.splice(targetIndex, 0, draggedItem);

    setMergeTicketsQueue(updatedQueue);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleMergeConfirm = async () => {
    if (mergeTicketsQueue.length < 2) {
      openNotification("warning", `Select at least 2 tickets to ${isLinkMode ? "link" : "merge"}.`);
      return;
    }
    setSubmitting(true);
    try {
      const isLink = isLinkMode;
      const res = isLink
        ? await TicketServices.linkTickets({
          ticketIds: mergeTicketsQueue.map((t) => t.value),
        })
        : await TicketServices.mergeTickets({
          parentTicketId: mergeTicketsQueue[0].value,
          childTicketIds: mergeTicketsQueue.slice(1).map((t) => t.value),
          parentStatus: mergeParentStatus === 0 ? mergeTicketsQueue[0].status : mergeParentStatus,
          childStatus: mergeChildStatus,
          mergeType,
        });

      if (res?.status) {
        openNotification("success", `Tickets ${isLink ? "linked" : "merged"} successfully.`);
        setIsMergeOpen(false);
        setMergeTicketsQueue([]);
        setMergeParentStatus(0);
        setMergeChildStatus(TicketStatus.Closed);
        setMergeType(MergeType.SeparateThreads);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        fetchData(pagingItems.currentPage, pagingItems.pageSize);
      } else {
        openNotification("error", res?.message || `${isLink ? "Link" : "Merge"} failed.`);
      }
    } catch (err) {
      openNotification("error", `Error ${isLinkMode ? "linking" : "merging"} tickets.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchStatusConfirm = async () => {
    if (!batchStatusValues.status) {
      openNotification("warning", "Please select a status.");
      return;
    }
    if (batchStatusValues.status === TicketStatus.Closed && (!batchStatusValues.closeReason || batchStatusValues.closeReason === CloseReason.Unknown)) {
      openNotification("warning", "Please select a closed reason.");
      return;
    }
    if (
      (batchStatusValues.status !== TicketStatus.Closed || batchStatusValues.closeReason === CloseReason.Other) &&
      batchStatusValues.customCloseReason?.trim().length < 5
    ) {
      openNotification("warning", "Please enter a reason (min 5 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const result = await TicketServices.updateTicketStatus({
        ticketIds: batchStatusValues.ticketIds,
        status: batchStatusValues.status,
        closeReason: batchStatusValues.closeReason,
        customCloseReason: batchStatusValues.customCloseReason,
        updateLinked: batchStatusValues.updateLinked,
      });

      if (result && result.status) {
        openNotification("success", `Successfully updated status for ${batchStatusValues.ticketIds.length} ticket(s).`);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setIsBatchStatusOpen(false);
        fetchData(pagingItems.currentPage, pagingItems.pageSize);
        fetchCounts();
        setBatchStatusValues(InitialBatchStatusValue);
      }
    } catch (err) {
      openNotification("error", "Error updating tickets status.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchAssignConfirm = async () => {
    if (!batchAssigneeIds || batchAssigneeIds.length === 0) {
      openNotification("warning", "Please select collaborators.");
      return;
    }
    const eligibleKeys = selectedRows
      .filter((t) => t.status !== TicketStatus.Closed)
      .map((t) => String(t.id));

    if (eligibleKeys.length === 0) {
      openNotification("warning", "Collaborators cannot be modified for closed tickets.");
      setIsBatchAssignOpen(false);
      return;
    }

    setSubmitting(true);
    try {
      const result = await TicketServices.updateCollaborators(eligibleKeys, batchAssigneeIds);
      if (result && result.status) {
        openNotification("success", `Collaborators updated successfully for ${eligibleKeys.length} ticket(s).`);
      } else {
        openNotification("error", result?.message || "Failed to update collaborators.");
      }
      setSelectedRowKeys([]);
      setSelectedRows([]);
      setIsBatchAssignOpen(false);
      setBatchAssigneeIds([]);
      fetchData(pagingItems.currentPage, pagingItems.pageSize);
    } catch (err) {
      openNotification("error", "Error assigning collaborators.");
    } finally {
      setSubmitting(false);
    }
  };

  const currentPageSizeRef = React.useRef(pagingItems.pageSize);
  currentPageSizeRef.current = pagingItems.pageSize;

  useEffect(() => {
    fetchData(1, currentPageSizeRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.priority, filters.inputRequired, filters.closeReason, filters.sortCol, filters.sortDir, dateRangeStr, filters?.practiceId, filters?.agentId, filters?.type, filters?.module]);

  const columnWidths = {
    sNo: 70,
    reference: 110,
    title: 200,
    practiceName: 150,
    company: 140,
    lastUpdated: 150,
    lastComment: 150,
    raisedBy: 120,
    agent: 130,
    collaborators: 150,
    module: 100,
    status: 100,
    priority: 120,
    sla: 120,
    type: 110,
    dueDate: 120,
    resolutionEta: 130,
    createdOn: 120,
    capsiTask: 130,
    actions: 100,
  };

  const rawColumns: any[] = [
    {
      key: "sNo",
      title: "S.No",
      toggleable: false,
      width: columnWidths.sNo,
      fixed: "left",
      render: (_: any, __: any, i: number) =>
        (pagingItems.currentPage - 1) * pagingItems.pageSize + i + 1,
    },
    {
      key: "reference",
      title: "Reference",
      toggleable: false,
      dataIndex: "number",
      width: columnWidths.reference,
      fixed: "left",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder:
        filters.sortCol === "N"
          ? filters.sortDir === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (num: any, record: any) => (
        <>
          <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <a onClick={() => navigate(`/tickets/${record.id}`)}>{num}</a>
            {record?.reopenedAt && (
              <Tooltip
                title={
                  record.reopenedAt
                    ? `Reopened on ${dayjs(record.reopenedAt).format("DD/MM/YYYY")}`
                    : "Reopened ticket"
                }
              >
                <ReloadOutlined
                  style={{ color: "#3b82f6", fontSize: 13, cursor: "pointer" }}
                />
              </Tooltip>
            )}
            {record?.status !== TicketStatus.Closed && record?.inputRequired && InputRequiredIcon}
            {record?.status !== TicketStatus.Closed && record?.dependencies && record?.dependencies?.length > 0 && (
              <Popover
                content={
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 120 }}
                  >
                    {(() => {
                      const merged = record.dependencies.filter((d: any) => d.type === TicketDependencyType.Merged);
                      const linked = record.dependencies.filter((d: any) => d.type === TicketDependencyType.Linked);
                      return (
                        <>
                          {merged.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span style={{ fontSize: 10, fontWeight: 600, color: token.colorTextSecondary }}>Merged tickets</span>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {merged.map((itm: any) => (
                                  <a
                                    key={itm.id}
                                    onClick={() => navigate(`/tickets/${itm.id}`)}
                                  >
                                    {itm?.ticketRef ?? itm?.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          {linked.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span style={{ fontSize: 10, fontWeight: 600, color: token.colorTextSecondary }}>Linked tickets</span>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {linked.map((itm: any) => (
                                  <a
                                    key={itm.id}
                                    onClick={() => navigate(`/tickets/${itm.id}`)}
                                  >
                                    {itm?.ticketRef ?? itm?.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                }
                trigger="hover"
                placement="right"
                open={showMergedTickets === num}
                onOpenChange={() =>
                  setShowMergedTickets((prev) => (prev === "" ? num : ""))
                }
              >
                <a
                  style={{
                    marginLeft: 10,
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                >
                  (+{record?.dependencies?.length})
                </a>
              </Popover>
            )}
          </span>
        </>
      ),
    },
    {
      key: "title",
      title: "Title",
      ellipsis: true,
      dataIndex: "title",
      width: columnWidths.title,
      toggleable: false,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder:
        filters.sortCol === "Title"
          ? filters.sortDir === "asc"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      key: "capsiTask",
      title: "Capsitask",
      width: columnWidths.capsiTask,
      toggleable: true,
      render: (row: any) => {
        if (row.capsiTaskRef?.id && row.capsiTaskRef?.name) {
          return (
            <a
              href={`${process.env.REACT_APP_CAPSITASK_URL || "https://appuat.capsitask.com"}/task/${row.capsiTaskRef.id}?uKey=${row.capsiTaskRef.name}&source=board`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.capsiTaskRef.name}
            </a>
          );
        }
        return "-";
      },
    },
    {
      key: "practiceName",
      title: "Practice name",
      dataIndex: "practiceName",
      width: columnWidths.practiceName,
      ellipsis: true,
      render: (value: string) => value || "-",
      toggleable: true,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder:
        filters.sortCol === "PracticeName"
          ? filters.sortDir === "asc"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      key: "company",
      title: "Business name",
      width: columnWidths.company || 140,
      ellipsis: true,
      render: (row: any) => row.company?.name || "-",
      toggleable: true,
    },
    {
      key: "type",
      title: "Type",
      width: columnWidths.type,
      toggleable: true,
      render: (row: any) => {
        const val = row.issueType ?? row.type;
        const map: Record<number, string> = {
          1: "Bug",
          2: "Development",
          3: "Enhancement",
        };
        return map[val] || "-";
      },
    },
    {
      key: "status",
      title: "Status",
      width: columnWidths.status,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder:
        filters.sortCol === "Status"
          ? filters.sortDir === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (row: any) => {
        const map: any = {
          [TicketStatus.Open]: { color: "processing", text: "Open" },
          [TicketStatus.Closed]: { color: "default", text: "Closed" },
          [TicketStatus.UnderResearch]: { color: "cyan", text: "Under research" },
          [TicketStatus.InProgress]: { color: "purple", text: "In progress" },
          [TicketStatus.InTesting]: { color: "warning", text: "In testing" },
          [TicketStatus.ReadyForRelease]: { color: "success", text: "Ready for release" }
        };
        const s = map[row.status] || { color: "default", text: "Unknown" };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
      toggleable: false,
    },
    {
      key: "module",
      title: "Module",
      ellipsis: true,
      width: columnWidths.module,
      dataIndex: "module",
      render: (m: number) => TicketModuleDisplay[m as TicketModule] || "-",
      toggleable: true,
    },
    {
      key: "sla",
      title: "SLA plan",
      dataIndex: "priority",
      width: columnWidths.sla,
      render: (p: number) => p ? <Text>P{p}</Text> : "-",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder:
        filters.sortCol === "Priority"
          ? filters.sortDir === "asc"
            ? "ascend"
            : "descend"
          : null,
      toggleable: true,
    },
    {
      key: "priority",
      title: "Priority",
      dataIndex: "priority",
      width: columnWidths.priority,
      render: (p: number) => {
        const map: any = {
          4: { color: "blue", label: "Low" },
          3: { color: "green", label: "Medium" },
          2: { color: "orange", label: "High" },
          1: { color: "red", label: "Urgent" },
        };
        return  p ? <Tag color={map[p]?.color}>{map[p]?.label || "-"}</Tag> : '-';
      },
      toggleable: false,
    },
    {
      key: "dueDate",
      title: "Due date",
      width: columnWidths.dueDate,
      render: (row: any) =>
        row.dueDate ? dayjs(row.dueDate).format("DD/MM/YYYY") : "-",
      toggleable: true,
    },
    {
      key: "lastUpdated",
      title: "Last updated",
      width: columnWidths.lastUpdated,
      render: (row: any) =>
        row.updatedBy?.date
          ? dayjs(row.updatedBy.date).format("DD/MM/YYYY")
          : "-",
      toggleable: true,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder:
        filters.sortCol === "UpdatedBy.Date"
          ? filters.sortDir === "asc"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      key: "lastComment",
      title: "Last comment",
      ellipsis: true,
      width: columnWidths.lastComment,
      render: (row: any) => {
        const commentText = row?.comment;
        if (!commentText) return "-";
        const cleanText = commentText
          .replace(/rnrn|\r\n|rn|\n/g, " ")
          .replace(/<[^>]*>/g, " ")
          .replace(/&nbsp;/gi, " ")
          .replace(/\s+/g, " ")
          .trim();

        if (!cleanText) return "-";

        return (
          <div
            title={cleanText}
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {cleanText}
          </div>
        );
      },
      toggleable: true,
    },
    {
      key: "agent",
      title: "Agent",
      width: columnWidths.agent,
      render: (row: any) => row.assignedTo?.name || "-",
      toggleable: true,
    },
    {
      key: "raisedBy",
      title: "Raised by",
      ellipsis: true,
      width: columnWidths.raisedBy,
      render: (row: any) => row?.createdBy?.userName || "-",
      toggleable: true,
    },
    {
      key: "resolutionEta",
      title: "Resolution ETA",
      width: columnWidths.resolutionEta,
      render: (row: any) => {
        const resObj = row.resolved || row.Resolved;
        const eta =
          resObj?.resolutionETA ||
          resObj?.resolutionEta ||
          resObj?.ResolutionETA ||
          row.resolutionETA ||
          row.resolutionEta ||
          row.ResolutionETA;
        return eta ? dayjs(eta).format("DD/MM/YYYY") : "-";
      },
      toggleable: true,
    },
    {
      key: "collaborators",
      title: "Collaborators",
      width: columnWidths.collaborators,
      toggleable: true,
      ellipsis: true,
      render: (row: any) => {
        const list = row.supportCollaborators || row.collaborators;
        if (list && list.length > 0) {
          const names = list.map((c: any) => c.name || c.userName).join(", ");
          return (
            <Tooltip title={names}>
              <span>{names}</span>
            </Tooltip>
          );
        }
        return "-";
      },
    },
    {
      key: "createdOn",
      title: "Created on",
      width: columnWidths.createdOn,
      render: (row: any) => dayjs(row.createdBy?.date).format("DD/MM/YYYY"),
      toggleable: true,
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder:
        filters.sortCol === "CreatedBy.Date"
          ? filters.sortDir === "asc"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      key: "actions",
      title: "Actions",
      width: columnWidths.actions,
      toggleable: false,
      render: (row: any) => {
        const items = [];

        if (row.status !== TicketStatus.Closed) {
          items.push({
            key: "assignAgent",
            label: row.assignedTo ? "Update agent" : "Assign agent",
            icon: <TeamOutlined />,
          });

          items.push({
            key: "resolutionEta",
            label: row.resolved?.resolutionETA
              ? "Update resolution ETA"
              : "Set resolution ETA",
            icon: <ClockCircleOutlined />,
          });
        }

        if (row.status === TicketStatus.Closed) {
          items.push({
            key: "reopen",
            label: "Re-open",
            icon: <ReloadOutlined />,
          });
        } else {
          items.push({
            key: "close",
            label: "Close ticket",
            icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
          });
        }

        if (!row.inputRequired && row.status !== TicketStatus.Closed) {
          items.push({
            key: "requestInput",
            label: "Request input",
            icon: <ExclamationCircleOutlined />,
          });
        }

        if (items.length === 0) return null;

        return (
          row?.status === TicketStatus.Closed ?
            <Button type="text" icon={<ReloadOutlined />} onClick={() => handleActionMenuClick("reopen", row)} />
            :
            <Dropdown
              menu={{
                items,
                onClick: (info) => handleActionMenuClick(info.key, row),
              }}
              trigger={["click"]}
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
        );
      },
    },
  ];

  const { columns, components, columnSelector } = useResizableColumns({
    columns: rawColumns,
    storageKey: "ao_tickets_dashboard"
  });

  const statusOptions = [
    { label: "All Status", value: 0 },
    { label: "Active", value: TicketStatus.Active },
    { label: "Open", value: TicketStatus.Open },
    { label: "Closed", value: TicketStatus.Closed },
    { label: "Under research", value: TicketStatus.UnderResearch },
    { label: "In progress", value: TicketStatus.InProgress },
    { label: "In testing", value: TicketStatus.InTesting },
    { label: "Ready for release", value: TicketStatus.ReadyForRelease },
  ];

  const priorityOptions = [
    { label: "All Priority", value: 0 },
    { label: "P4", value: TicketPriority.P4 },
    { label: "P3", value: TicketPriority.P3 },
    { label: "P2", value: TicketPriority.P2 },
    { label: "P1", value: TicketPriority.P1 },
  ];

  const inputRequiredOptions = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
  ];

  const primaryActions: any[] = [
    // {
    //   label: "Ticket",
    //   icon: <PlusOutlined />,
    //   type: "primary",
    //   onClick: () => setIsCreateOpen(true),
    // },
    {
      label: "Download",
      icon: <DownloadOutlined />,
      type: "default",
      onClick: handleDownloadCSV,
    },
  ];



  const filterCriteria = [
    {
      key: "status",
      label: "Status",
      type: "select" as const,
      options: statusOptions.filter((o) => o.value !== 0),
      placeholder: "Select status",
    },
    {
      key: "priority",
      label: "Priority",
      type: "select" as const,
      options: priorityOptions.filter((o) => o.value !== 0),
      placeholder: "Select priority",
    },
    {
      key: "inputRequired",
      label: "Input required",
      type: "select" as const,
      options: inputRequiredOptions,
      placeholder: "Select input required",
    },
    {
      key: "practiceId",
      label: "Practice",
      type: "select" as const,
      options: allPracticeIds,
      placeholder: "Select practice",
    },
    {
      key: "agentId",
      label: "Agent",
      type: "select" as const,
      options: assigneeOptions,
      placeholder: "Select agent",
    },
    {
      key: "type",
      label: "Type",
      type: "select" as const,
      options: [
        { label: "Bug", value: TicketIssueType.Bug },
        { label: "Development", value: TicketIssueType.New },
        { label: "Enhancement", value: TicketIssueType.Enhancement },
      ],
      placeholder: "Select issue type",
    },
    {
      key: "module",
      label: "Module",
      type: "select" as const,
      options: [
        { label: "AML", value: TicketModule.AML },
        { label: "Billing & subscriptions", value: TicketModule.Billing },
        { label: "Bookkeeping", value: TicketModule.Books },
        { label: "CRM", value: TicketModule.CRM },
        { label: "Payroll", value: TicketModule.PAYE },
        { label: "Self assessment", value: TicketModule.SA },
        { label: "Tax and accounts", value: TicketModule.Accounts },
      ],
      placeholder: "Select module",
    },
    ...(filters.status !== TicketStatus.Active
      ? [
          {
            key: "closeReason",
            label: "Closed reason",
            type: "select" as const,
            options: CLOSED_REASONS,
            placeholder: "Select reason",
          },
        ]
      : []),
  ];

  const activeFilters = {
    status: filters.status > -1 ? filters.status : undefined,
    priority: filters.priority !== 0 ? filters.priority : undefined,
    inputRequired: filters.inputRequired !== "all" ? filters.inputRequired : undefined,
    closeReason: filters.status !== TicketStatus.Active && filters.closeReason ? filters.closeReason : undefined,
    practiceId: filters.practiceId !== 0 ? filters.practiceId : undefined,
    agentId: filters?.agentId ? filters?.agentId : undefined,
    type: filters?.type ? filters?.type : undefined,
    module: filters?.module ? filters?.module : undefined,
  };

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    const nextStatus = newFilters.status !== undefined ? newFilters.status : -1;
    setFilters((prev) => ({
      ...prev,
      status: nextStatus,
      priority: newFilters.priority !== undefined ? newFilters.priority : 0,
      inputRequired: newFilters.inputRequired !== undefined ? newFilters.inputRequired : "all",
      closeReason: nextStatus === TicketStatus.Active ? undefined : newFilters.closeReason,
      practiceId: newFilters.practiceId !== undefined ? newFilters.practiceId : 0 ,
      agentId: newFilters?.agentId,
      type: newFilters?.type || 0,
      module: newFilters?.module || 0,
    }));
  };

  const progressStatuses = KPI_STATUSES.filter(k => k.status !== -1);
  const kpiTotal = progressStatuses.reduce((s, k) => s + (counts[k.status] ?? 0), 0);

  return (
    <PageLayout maxWidth="100%">
      <PageHeader title="Support tickets" subtitle="Track, manage and resolve client support requests" />
      <div>
        <Row gutter={[10, 10]}>
          {KPI_STATUSES.map(k => (
            <Col key={k.status} xs={12} sm={8} md={6} lg={4} xl={3} xxl={3}>
              <KpiCard
                kpi={k}
                count={counts[k.status] ?? 0}
                loading={countsLoading}
                active={filters.status === k.status}
                isDark={isDark}
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    status: prev.status === k.status ? -1 : k.status,
                  }))
                }
              />
            </Col>
          ))}
        </Row>

        {/* ── Distribution bar ────────────────────────────────────────────── */}
        {kpiTotal > 0 && !countsLoading && (() => {
          const pcts = calculatePercentages(counts, progressStatuses);
          return (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", height: 5, borderRadius: 4, overflow: "hidden", gap: 1 }}>
                {progressStatuses.filter(k => (counts[k.status] ?? 0) > 0).map(k => (
                  <Tooltip key={k.status} title={`${k.label}: ${counts[k.status]} (${pcts[k.status] ?? 0}%)`}>
                    <div
                      style={{
                        flex: counts[k.status] / kpiTotal,
                        background: k.fg,
                        cursor: "pointer",
                        opacity: filters.status > -1 && filters.status !== k.status ? 0.25 : 1,
                        transition: "opacity 0.2s",
                      }}
                      onClick={() =>
                        setFilters(prev => ({ ...prev, status: prev.status === k.status ? -1 : k.status }))
                      }
                    />
                  </Tooltip>
                ))}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 6 }}>
                {progressStatuses.filter(k => (counts[k.status] ?? 0) > 0).map(k => (
                  <div key={k.status} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => setFilters(prev => ({ ...prev, status: prev.status === k.status ? -1 : k.status }))}
                  >
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: k.fg, flexShrink: 0 }} />
                    <Text style={{ fontSize: 11, color: token.colorTextSecondary }}>
                      {k.label} ({pcts[k.status] ?? 0}%)
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            primaryActions={primaryActions}
            onSearch={(val: string) => setFilters({ ...filters, search: val })}
            searchPlaceholder="Search"
            filterCriteria={filterCriteria}
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            onRefresh={() => fetchData(pagingItems.currentPage, pagingItems.pageSize)}
            extraBeforeFilters={
              <DatePicker.RangePicker
                format="DD/MM/YYYY"
                value={filters.dateRange}
                onChange={(val) =>
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: val as [dayjs.Dayjs, dayjs.Dayjs] | null,
                  }))
                }
                allowClear
                placeholder={["From date", "To date"]}
                style={{ width: "100%", maxWidth: 200, minWidth: 160 }}
              />
            }
            extra={columnSelector}
          />
        </div>
        {selectedRowKeys.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
              background: isDark ? "#112a45" : "#F0F7FF",
              border: `1.5px solid ${isDark ? "#174573" : "#BAE7FF"}`,
              borderRadius: 8,
              marginBottom: 16,
              marginLeft: 16,
              marginRight: 16,
              animation: "fadeIn 0.2s ease-in-out",
            }}
          >
            <Space>
              <span style={{ color: isDark ? "#A5C8F7" : "#0050B3", fontWeight: 600, fontSize: 13 }}>
                {selectedRowKeys.length} {selectedRowKeys.length === 1 ? "ticket" : "tickets"} selected
              </span>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setSelectedRowKeys([]);
                  setSelectedRows([]);
                }}
                style={{ padding: 0, fontWeight: 500 }}
              >
                Clear selection
              </Button>
            </Space>
            <Space size="small">
              <Button
                type="primary"
                icon={<LinkOutlined />}
                disabled={selectedRowKeys.length < 2}
                onClick={() => openMergeOrLinkModal(true)}
                style={{ borderRadius: 6 }}
              >
                Link tickets
              </Button>
              <Button
                icon={<BranchesOutlined />}
                disabled={selectedRowKeys.length < 2}
                onClick={() => openMergeOrLinkModal(false)}
                style={{ borderRadius: 6 }}
              >
                Merge tickets
              </Button>
              <Tooltip title={selectedRows.some(t => t.status === TicketStatus.Closed) ? "Collaborators cannot be modified for closed tickets" : ""}>
                <Button
                  icon={<TeamOutlined />}
                  disabled={selectedRowKeys.length === 0 || selectedRows.some(t => t.status === TicketStatus.Closed)}
                  onClick={() => {
                    if (selectedRows.some((t) => t.status === TicketStatus.Closed)) {
                      openNotification("warning", "Collaborators cannot be modified for closed tickets.");
                      return;
                    }
                    const existingIds = Array.from(
                      new Set(
                        selectedRows.flatMap((t) =>
                          (t.supportCollaborators || t.collaborators || []).map((c: any) => String(c.id || c.userId || c.value))
                        )
                      )
                    ).filter(Boolean);
                    selectedRows.length === 1 && setBatchAssigneeIds(existingIds);
                    setIsBatchAssignOpen(true);
                  }}
                  style={{ borderRadius: 6 }}
                >
                  Collaborators
                </Button>
              </Tooltip>
              <Button
                icon={<SyncOutlined />}
                disabled={selectedRowKeys.length === 0 || getBatchStatusOptions().length === 0}
                onClick={() => {
                  const ids = selectedRows.map(r => String(r.id || r.key)).filter(Boolean);
                  const finalIds = ids.length > 0 ? ids : selectedRowKeys.map(k => String(k));
                  setBatchStatusValues({
                    ...InitialBatchStatusValue,
                    ticketIds: finalIds,
                  });
                  setIsBatchStatusOpen(true);
                }}
                style={{ borderRadius: 6 }}
              >
                Update status
              </Button>
            </Space>
          </div>
        )}

        <Table
          style={{
            margin: "0 16px",
            marginBottom: 16,
          }}
          rowKey="id"
          rowSelection={{
            fixed: true,
            selectedRowKeys,
            onChange: (keys: React.Key[], rows: any[]) => {
              const filteredRows = rows.filter(r => r.status !== TicketStatus.Closed);
              const filteredKeys = filteredRows.map(r => r.id);
              setSelectedRowKeys(filteredKeys);
              setSelectedRows(filteredRows);
            },
          }}
          dataSource={data}
          columns={columns}
          components={components}
          size="small"
          className="border-top-table"
          scroll={{
            x: "max-content",
            y: (data?.length || 0) > 10 ? window.innerHeight - 256 : undefined
          }}
          loading={{
            spinning: loading,
            indicator: (
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />}
              />
            ),
          }}
          onChange={(paginationInfo, _tableFilters, sorter: any, extra: any) => {
            if (extra?.action === "sort") {
              const colKeyMap: Record<string, string> = {
                reference: "N",
                title: "Title",
                practiceName: "PracticeName",
                lastUpdated: "UpdatedBy.Date",
                status: "Status",
                priority: "Priority",
                sla: "Priority",
                createdOn: "CreatedBy.Date",
              };
              const s = Array.isArray(sorter) ? sorter[0] : sorter;
              if (s) {
                const key = String(s.columnKey || s.field || s.column?.key || "");
                const newCol = (key && colKeyMap[key]) ? colKeyMap[key] : filters.sortCol;
                const newDir: "asc" | "desc" = s.order === "ascend" ? "asc" : (s.order === "descend" ? "desc" : (filters.sortDir === "asc" ? "desc" : "asc"));
                setFilters(prev => ({ ...prev, sortCol: newCol, sortDir: newDir }));
              }
            } else if (extra?.action === "paginate") {
              fetchData(paginationInfo.current!, paginationInfo.pageSize!);
            }
          }}
          pagination={{
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            current: pagingItems.currentPage,
            pageSize: pagingItems.pageSize,
            total: pagingItems.totalRecords,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />

        <Modal
          title={"Mark ticket as closed"}
          open={isModalOpen}
          onOk={handleAction}
          onCancel={closeModal}
          okText="Confirm"
          cancelText="Cancel"
          confirmLoading={submitting}
          okButtonProps={{
            disabled: selectedCloseReason === CloseReason.Unknown || (selectedCloseReason === CloseReason.Other && reason.trim().length < 5)
          }}
        >
          <p style={{ marginBottom: 8 }}>
            Provide a resolution summary for ticket
            <strong> #{selectedRow?.number}</strong>:
          </p>
          <Select
            placeholder="Select a closed reason"
            style={{ width: "100%", marginBottom: 8 }}
            options={CLOSED_REASONS}
            onChange={(val) => {
              setSelectedCloseReason(val);
              const opt = CLOSED_REASONS.find(r => r.value === val);
              if (val !== CloseReason.Other) {
                setReason(opt ? opt.label : "");
              } else {
                setReason("");
              }
            }}
            value={CLOSED_REASONS.some(r => r.value === selectedCloseReason) ? selectedCloseReason : undefined}
            disabled={submitting}
          />
          {selectedCloseReason === CloseReason.Other && (
            <TextArea
              rows={4}
              placeholder="Enter reason (min 5 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 3 }}
            />
          )}
          {selectedRow?.dependencies && selectedRow?.dependencies?.length > 0 && <Checkbox checked={updateLinkedTickets} onChange={(e) => setUpdateLinkedTickets(e.target.checked)} >Close all linked tickets.</Checkbox>}
        </Modal>

        {/* Merge Tickets Modal */}
        {isMergeOpen && <Modal
          title={isLinkMode ? "Link tickets" : "Merge tickets"}
          open={true}
          onOk={handleMergeConfirm}
          onCancel={() => {
            setIsMergeOpen(false);
            setMergeTicketsQueue([]);
            setMergeParentStatus(0);
            setMergeChildStatus(TicketStatus.Closed);
            setMergeType(MergeType.SeparateThreads);
            setMergeSearchValue("");
            setMergeTicketOptions([]);
          }}
          okText={isLinkMode ? "Link" : "Merge"}
          cancelText="Cancel"
          confirmLoading={submitting}
          width={700}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <label>{isLinkMode ? "Link tickets to keep a reference to similar issues. Update the status and post threads directly from one ticket." : "Merge tickets with the same issue into one ticket. Keep all related information in one place."}</label>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Search tickets</label>
              <Select
                showSearch
                value={mergeSearchValue}
                style={{ width: "100%" }}
                placeholder="Search ticket"
                defaultActiveFirstOption={false}
                showArrow={true}
                filterOption={(input, option: any) =>
                  String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onSearch={(val) => {
                  setMergeSearchValue(val);
                  if (val.trim().length >= 2) {
                    handleSearchMergeTicket(val);
                  }
                }}
                onChange={(val, option: any) => {
                  if (option) {
                    if (mergeTicketsQueue.some(t => t.value === val)) {
                      openNotification("warning", `Ticket already added to ${isLinkMode ? "link" : "merge"} queue.`);
                    } else {
                      setMergeTicketsQueue(prev => [...prev, option]);
                    }
                  }
                  setMergeSearchValue(undefined);
                }}
                notFoundContent={mergeLoadingOptions ? <Spin size="small" /> : null}
                options={mergeTicketOptions}
              />
            </div>

            {mergeTicketsQueue.length > 0 && (
              <div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 6, fontWeight: "bold" }}>
                  {isLinkMode ? "Link queue" : "Merge queue"}
                  {!isLinkMode && <Tooltip title="Drag ticket(s) to change order.">
                    <InfoCircleOutlined style={{ color: token.colorTextSecondary, fontSize: 13, cursor: "pointer" }} />
                  </Tooltip>}
                </label>
                <div style={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  border: `1px solid ${token.colorBorderSecondary}`,
                  borderRadius: token.borderRadiusLG,
                }}>
                  <List
                    bordered={false}
                    dataSource={mergeTicketsQueue}
                    renderItem={(item, index) => (
                      <List.Item
                        actions={[
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => setMergeTicketsQueue(prev => prev.filter((_, i) => i !== index))}
                          />
                        ]}
                        style={{
                          cursor: draggedIndex !== null ? "grabbing" : "grab",
                          opacity: draggedIndex === index ? 0.5 : 1,
                          background: dragOverIndex === index ? `${token.colorPrimaryBg}` : "transparent",
                          borderBottom: index === mergeTicketsQueue.length - 1 ? "none" : `1px solid ${token.colorBorderSecondary}`,
                          padding: "12px 16px",
                          userSelect: "none",
                          transition: "all 0.2s ease",
                        }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, index)}
                      >
                        <Space>
                          <HolderOutlined style={{ color: token.colorTextDescription, cursor: draggedIndex !== null ? "grabbing" : "grab", marginRight: 4 }} />
                          {!isLinkMode && <Tag color={index === 0 ? "blue" : "default"}>
                            {index === 0 ? "Parent" : `Child ${index}`}
                          </Tag>}
                          <span>{item.label}</span>
                        </Space>
                      </List.Item>
                    )}
                  />
                </div>
              </div>
            )}

            {!isLinkMode && mergeTicketsQueue.length > 0 && (
              <Row gutter={16}>
                <Col span={12}>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Parent ticket status</label>
                  <Select
                    style={{ width: "100%" }}
                    value={mergeParentStatus}
                    options={getMergeParentStatusOptions()}
                    onChange={(val) => setMergeParentStatus(val)}
                  />
                </Col>
                <Col span={12}>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Child tickets status</label>
                  <Select
                    style={{ width: "100%" }}
                    disabled
                    value={mergeChildStatus}
                    options={[
                      { label: "Closed", value: TicketStatus.Closed },
                    ]}
                    onChange={(val) => setMergeChildStatus(val)} 
                  />
                </Col>
              </Row>
            )}

            {!isLinkMode && mergeTicketsQueue.length > 0 && (
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Thread {isLinkMode ? "link" : "merge"} type</label>
                <Radio.Group
                  value={mergeType}
                  onChange={(e) => setMergeType(e.target.value)}
                >
                  <Radio value={MergeType.SeparateThreads}>Separate threads</Radio>
                  <Radio value={MergeType.CombineThreads}>Combine threads</Radio>
                </Radio.Group>
              </div>
            )}
          </Space>
        </Modal>}

        {/* Batch Status Update Modal */}
        <Modal
          title="Update status"
          open={isBatchStatusOpen}
          onOk={handleBatchStatusConfirm}
          onCancel={() => {
            setIsBatchStatusOpen(false);
            setBatchStatusValues(InitialBatchStatusValue);
          }}
          okText="Confirm"
          cancelText="Cancel"
          confirmLoading={submitting}
          okButtonProps={{
            disabled:
              batchStatusValues.status === TicketStatus.Unknown ||
              (batchStatusValues.status === TicketStatus.Closed
                ? (batchStatusValues.closeReason === CloseReason.Other
                  ? batchStatusValues.customCloseReason.trim().length < 5
                  : !batchStatusValues.closeReason || batchStatusValues.closeReason === CloseReason.Unknown)
                : batchStatusValues.customCloseReason.trim().length < 5)
          }}
        >
          <p style={{ marginBottom: 12 }}>
            Update status for <strong>{selectedRowKeys.length}</strong> selected ticket(s)
          </p>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>New status</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select status"
                value={batchStatusValues.status || undefined}
                options={getBatchStatusOptions().filter(r => r.value !== TicketStatus.Closed)}
                onChange={(val) =>
                  setBatchStatusValues(prev => ({
                    ...prev,
                    status: val,
                    closeReason: val !== TicketStatus.Closed ? CloseReason.Unknown : prev.closeReason,
                    customCloseReason: val !== TicketStatus.Closed ? '' : prev.customCloseReason,
                  }))
                }
              />
            </div>
            {batchStatusValues.status === TicketStatus.Closed && (
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Reason summary</label>
                <Select
                  placeholder="Select a closed reason"
                  style={{ width: "100%", marginBottom: 8 }}
                  options={CLOSED_REASONS}
                  onChange={(val) => {
                    const opt = CLOSED_REASONS.find(r => r.value === val);
                    const label = opt ? opt.label : '';
                    setBatchStatusValues(prev => ({
                      ...prev,
                      closeReason: val,
                      customCloseReason: val === CloseReason.Other ? '' : label,
                    }));
                  }}
                  value={CLOSED_REASONS.some(r => r.value === batchStatusValues.closeReason) ? batchStatusValues.closeReason : undefined}
                  disabled={submitting}
                />
              </div>
            )}
            {(batchStatusValues.status !== TicketStatus.Closed || batchStatusValues.closeReason === CloseReason.Other) && (
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>
                  {batchStatusValues.status === TicketStatus.Closed ? "Custom reason" : "Reason summary"}
                </label>
                <TextArea
                  rows={4}
                  placeholder={batchStatusValues.status === TicketStatus.Closed ? "Enter custom reason (min 5 characters)" : "Provide a reason detailing why you are updating the ticket status (min 5 characters)"}
                  value={batchStatusValues.customCloseReason}
                  onChange={(e) => setBatchStatusValues(prev => ({ ...prev, customCloseReason: e.target.value }))}
                  autoSize={{ minRows: 3, maxRows: 5 }}
                />
              </div>
            )}
          </Space>
        </Modal>

        {/* Batch Assign Collaborators Modal */}
        {isBatchAssignOpen && <Modal
          title="Assign collaborators"
          open
          onOk={handleBatchAssignConfirm}
          onCancel={() => {
            setIsBatchAssignOpen(false);
            setBatchAssigneeIds([]);
          }}
          okText="Confirm"
          cancelText="Cancel"
          confirmLoading={submitting}
          okButtonProps={{ disabled: batchAssigneeIds.length === 0 || selectedRows.some(t => t.status === TicketStatus.Closed) }}
        >
          {selectedRows.some(t => t.status === TicketStatus.Closed) && (
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 6, color: "#991B1B", fontSize: 12 }}>
              Closed tickets detected in selection. Collaborators cannot be modified for closed tickets.
            </div>
          )}
          <p style={{ marginBottom: 12 }}>
            Assign the selected <strong>{selectedRows.filter(t => t.status !== TicketStatus.Closed).length}</strong> tickets to support collaborator(s)
          </p>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Support collaborators</label>
            <Select
              mode="multiple"
              maxTagCount={0}
              style={{ width: "100%" }}
              placeholder="Select collaborators"
              loading={assigneeLoading}
              value={batchAssigneeIds}
              onChange={(val) => setBatchAssigneeIds(val)}
              options={assigneeOptions}
              showSearch
              optionFilterProp="label"
              disabled={selectedRows.every(t => t.status === TicketStatus.Closed)}
            />
            {batchAssigneeIds.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <label style={{ display: "block", marginBottom: 6, fontSize: 12, color: token.colorTextSecondary, fontWeight: 500 }}>
                  Selected collaborators ({batchAssigneeIds.length})
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: "120px", overflowY: "auto" }}>
                  {batchAssigneeIds.map((id) => {
                    const opt = assigneeOptions.find((o) => String(o.value) === String(id));
                    const label = opt ? opt.label : id;
                    return (
                      <Tag
                        key={id}
                        closable
                        onClose={() => setBatchAssigneeIds((prev) => prev.filter((i) => String(i) !== String(id)))}
                        style={{ padding: "4px 8px", borderRadius: 4, display: "inline-flex", alignItems: "center" }}
                      >
                        {label}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Modal>}

        {/* Single Reopen Modal */}
        <Modal
          title="Reopen ticket"
          open={isReopenOpen}
          onOk={handleReopenConfirm}
          onCancel={() => {
            setIsReopenOpen(false);
            setReopenReason("");
            setActionTicket(null);
          }}
          okText="Confirm"
          cancelText="Cancel"
          confirmLoading={submitting}
          okButtonProps={{ disabled: reopenReason.trim().length < 5 }}
        >
          <p style={{ marginBottom: 8 }}>
            Provide a reason for reopening ticket <strong>#{actionTicket?.number}</strong>:
          </p>
          <TextArea
            rows={4}
            placeholder="Enter reason (min 5 characters)"
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 3 }}
          />
        </Modal>

        {/* Single Request Input Modal */}
        <Modal
          title="Request input"
          open={isInputReqOpen}
          onOk={handleInputReqConfirm}
          onCancel={() => {
            setIsInputReqOpen(false);
            setInputReqReason("");
            setActionTicket(null);
          }}
          okText="Request input"
          cancelText="Cancel"
          confirmLoading={submitting}
          okButtonProps={{ disabled: !inputReqReason.trim() }}
        >
          <p style={{ marginBottom: 8 }}>
            Provide the message required from the client.
          </p>
          <TextArea
            rows={4}
            placeholder="Enter reason"
            value={inputReqReason}
            onChange={(e) => setInputReqReason(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 3 }}
          />
        </Modal>

        {/* Single Assign Agent Modal */}
        <Modal
          title="Assign Agent"
          open={isSingleAssignOpen}
          onOk={handleSingleAssignConfirm}
          onCancel={() => {
            setIsSingleAssignOpen(false);
            setSingleAssigneeId("");
            setActionTicket(null);
          }}
          okText="Assign"
          cancelText="Cancel"
          confirmLoading={submitting}
          okButtonProps={{ disabled: !singleAssigneeId }}
        >
          <p style={{ marginBottom: 12 }}>
            Assign ticket <strong>#{actionTicket?.number}</strong> to a support agent:
          </p>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Support agent</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select an agent"
              loading={assigneeLoading}
              value={singleAssigneeId || undefined}
              onChange={(val) => setSingleAssigneeId(val)}
              options={assigneeOptions}
              showSearch
              optionFilterProp="label"
            />
          </div>
        </Modal>

        {/* Single Resolution ETA Modal */}
        <Modal
          title="Set resolution ETA"
          open={isSingleEtaOpen}
          onOk={handleSingleEtaConfirm}
          onCancel={() => {
            setIsSingleEtaOpen(false);
            setSingleEtaDate(null);
            setActionTicket(null);
          }}
          width={400}
          okText="Set ETA"
          cancelText="Cancel"
          confirmLoading={submitting}
          okButtonProps={{ disabled: !singleEtaDate }}
        >
          <p style={{ marginBottom: 12 }}>
            Set a resolution ETA for ticket <strong>#{actionTicket?.number}</strong>:
          </p>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: "bold" }}>Resolution ETA</label>
            <DatePicker
              placeholder="Select date"
              value={singleEtaDate}
              format="DD/MM/YYYY"
              minDate={dayjs()}
              disabledDate={(current) => current && current.isBefore(dayjs().startOf("day"))}
              onBlur={(e: any) => {
                if (e?.target?.value) {
                  const selectedDay = dayjs(e?.target?.value, DateFormats, true);
                  if (selectedDay.isValid() && !selectedDay.isBefore(dayjs().startOf("day"))) {
                    setSingleEtaDate(selectedDay);
                  } else setSingleEtaDate(dayjs())
                }
              }}
              onChange={(val) => setSingleEtaDate(val)}
              allowClear={false}
            />
          </div>
        </Modal>



        <CreateTicketPanel
          visible={isCreateOpen}
          onClose={(refresh) => {
            setIsCreateOpen(false);
            if (refresh) {
              void fetchCounts();
              void fetchData();
            }
          }}
        />
      </SectionCard>
    </PageLayout>
  );
};

export default AOTicketDashboard;
